import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import MePosDateFilter from '../mePosFilters/mePosDateFilter';
import SaleStatisticsTypeSelect from './SaleStatisticsTypeSelect';
import { useDispatch } from 'react-redux';
import EmployeeSelect from './EmployeeSelect';
import { DEFAULT_STATISTICS_TYPE, START_DATE_RANGE } from './constants';
import { resetSaleFilters, setSaleFilters } from '../../../../redux/slices/saleFiltersSlice';
import useSalesRequest from '../../../../hooks/useSaleRequest';
import BrandSelect from './BrandSelect';
import WarehouseSelect from './WarehouseSelect';
import TextInputWithAutoComplete from './TextInputWithAutoComplete';
import { CategoryPickerComponent } from '@me-pos/category-picker';
import { formatRangeToRequestDates, normalizeStringList } from './helpers';
import { createDebounce } from '../../sales/utils/helpers';

const MePosSaleFilters = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { getOptions, getSalesCount } = useSalesRequest();

  const [dateRange, setDateRange] = useState(START_DATE_RANGE);
  const [selectedStatisticType, setSelectedStatisticType] = useState(DEFAULT_STATISTICS_TYPE);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedProductSkuBarcode, setSelectedProductSkuBarcode] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  const [currentIdCatFilter, setCurrentIdCatFilter] = useState({
    categoryId: null, subCategoryId: null, categoryName: '', subCategoryName: ''
  });
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [currentCatNameFilter, setCurrentCatNameFilter] = useState('');

  const [productSkuBarcodeSuggestions, setProductSkuBarcodeSuggestions] = useState(null);
  const [options, setOptions] = useState({ employees: [], categories: [], brands: [], warehouses: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selfPermission, setSelfPermission] = useState(false);
  const [lockedSelfEmployee, setLockedSelfEmployee] = useState(null);

  const abortRef = useRef(null);
  const mountAutoAppliedRef = useRef(false);
  const debouncedRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (dropdownRef.current && !dropdownRef.current.contains(target) && target.id !== 'product-category-field') {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const syncSelfPermissionEmployee = (response) => {
    const hasSelfPermission = response?.selfPermission === true;
    const employees = response?.employees ?? [];

    setSelfPermission(hasSelfPermission);

    if (!hasSelfPermission) {
      setLockedSelfEmployee(null);
      return null;
    }
    if (employees.length !== 1) {
      setLockedSelfEmployee(null);
      return null;
    }

    const [selfEmployee] = employees;
    setLockedSelfEmployee(selfEmployee);
    setSelectedEmployee(selfEmployee);

    return selfEmployee;
  };

  const loadOptions = async ({ startDate, endDate, extraQueryMust = [] }) => {
    if (!startDate || !endDate) {
      setOptions({ employees: [], categories: [], brands: [], warehouses: [] });
      setSelectedWarehouse(null);
      return { employees: [], categories: [], brands: [], warehouses: [], selfPermission: false };
    }
    if (abortRef.current) try {
      abortRef.current.abort();
    } catch (e) {
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoadingOptions(true);
    try {
      const response = await getOptions({
        startDate: startDate, endDate: endDate, extraQueryMust, signal: controller.signal
      });
      const employees = response.employees ?? [];
      setOptions({
        employees,
        categories: response.categories ?? [],
        brands: response.brands ?? [],
        warehouses: response.warehouses ?? []
      });
      syncSelfPermissionEmployee({ ...response, employees });
      if (response.mergedSuggestions) {
        const productSkuBarcodeSuggestionsList = normalizeStringList(response.mergedSuggestions);
        setProductSkuBarcodeSuggestions(productSkuBarcodeSuggestionsList);
      }
      return { ...response, employees };
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return { employees: [], categories: [], brands: [], warehouses: [], selfPermission: false };
      console.error('getOptions error', err);
      setOptions({ employees: [], categories: [], brands: [], warehouses: [] });
      return { employees: [], categories: [], brands: [], warehouses: [], selfPermission: false };
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    debouncedRef.current = createDebounce(async ({ startDate, endDate }) => {
      await loadOptions({ startDate, endDate });

    }, 400);
    return () => debouncedRef.current.cancel();
  }, []);

  // mount: load options + check if data exist for default period, if yes -> auto apply
  useEffect(() => {
    const { startDate, endDate } = formatRangeToRequestDates(dateRange);
    const loadOptionsPromise = loadOptions({ startDate, endDate }).catch(() => ({
      employees: [], categories: [], brands: [], warehouses: [], selfPermission: false
    }));
    (async () => {
      if (mountAutoAppliedRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const optionsResponse = await loadOptionsPromise;
        const autoEmployee = optionsResponse?.selfPermission === true && optionsResponse?.employees?.length === 1
          ? optionsResponse.employees[0]
          : null;

        const count = await getSalesCount({ startDate: startDate, endDate: endDate, signal: controller.signal });
        if (count && count > 0) {
          const payload = {
            periodStart: startDate,
            periodEnd: endDate,
            statisticType: selectedStatisticType,
            employee: optionsResponse?.selfPermission === true ? autoEmployee : null,
            category: null,
            brand: null,
            productSkuBarcode: null,
            warehouse: null
          };
          dispatch(setSaleFilters(payload));
          mountAutoAppliedRef.current = true;
        } else {
          mountAutoAppliedRef.current = false;
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error('getSalesCount error', err);
      }
    })();

    return () => {
      if (abortRef.current) try {
        abortRef.current.abort();
      } catch (e) {
      }
    };
  }, []);

  // when user changes local dates -> preview options (debounced)
  useEffect(() => {
    const { startDate, endDate } = formatRangeToRequestDates(dateRange);
    if (startDate && endDate) {
      debouncedRef.current({ startDate, endDate });
    }
  }, [dateRange]);

  const handleCategoryChange = (categoryId, categoryName) => {
    setCurrentIdCatFilter({ categoryId, categoryName, subCategoryId: null, subCategoryName: '' });
  };
  const handleSubCategoryChange = (categoryId, categoryName, subCategoryId, subCategoryName) => {
    setCurrentIdCatFilter((prevState) => ({
      ...prevState,
      categoryId: categoryId,
      categoryName: categoryName,
      subCategoryId: subCategoryId,
      subCategoryName: subCategoryName
    }));
  };
  const handleApplyFilter = () => {
    const { startDate, endDate } = formatRangeToRequestDates(dateRange);
    const employee = selfPermission ? lockedSelfEmployee : selectedEmployee;
    const payload = {
      periodStart: startDate,
      periodEnd: endDate,
      statisticType: selectedStatisticType,
      employee,
      category: currentIdCatFilter?.subCategoryId ? currentIdCatFilter?.subCategoryId : currentIdCatFilter?.categoryId,
      categoryLabel: currentIdCatFilter,
      brand: selectedBrand,
      productSkuBarcode: selectedProductSkuBarcode,
      warehouse: selectedWarehouse
    };
    dispatch(setSaleFilters(payload));
  };

  const resetPickedFlags = (categories = []) => {
    if (!Array.isArray(categories)) return [];
    return categories.map(cat => ({
      ...cat,
      isPicked: false,
      isSubcategoryPiked: false,
      subcategoryList: Array.isArray(cat.subcategoryList) ? cat.subcategoryList.map(sub => ({
        ...sub,
        isPicked: false
      })) : []
    }));
  };

  const handleResetFilter = () => {
    const { startDate, endDate } = formatRangeToRequestDates(START_DATE_RANGE);
    const resetEmployee = selfPermission ? lockedSelfEmployee : null;

    setOptions(prev => ({
      ...prev, categories: resetPickedFlags(prev.categories)
    }));
    setDateRange(START_DATE_RANGE);
    setSelectedStatisticType(DEFAULT_STATISTICS_TYPE);
    setSelectedEmployee(resetEmployee);
    setSelectedBrand(null);
    setSelectedProductSkuBarcode(null);
    setSelectedWarehouse(null);
    setCurrentIdCatFilter({
      categoryId: null, categoryName: '', subCategoryId: null, subCategoryName: ''
    });
    setCurrentCatNameFilter('');
    setIsPickerOpen(false);

    if (selfPermission) {
      dispatch(setSaleFilters({
        periodStart: startDate,
        periodEnd: endDate,
        statisticType: DEFAULT_STATISTICS_TYPE,
        employee: resetEmployee,
        category: null,
        categoryLabel: null,
        brand: null,
        productSkuBarcode: null,
        warehouse: null
      }));
      return;
    }

    dispatch(resetSaleFilters());
  };

  return (
    <div className="d-flex flex-column justify-content-between mb-5">
      <Row className="gx-3 mb-md-3">
        <Col md={6} className="mb-3 mb-md-0">
          <div className="position-relative">
            <MePosDateFilter
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDateRange={START_DATE_RANGE}
              subLabel='salesFilters.Operation date range'
              hideSubLabel={false}
            />
          </div>
        </Col>
        <Col md={6} className="mb-3 mb-md-0">
          <SaleStatisticsTypeSelect
            selectedType={selectedStatisticType}
            setSelectedType={setSelectedStatisticType}
          />
        </Col>
      </Row>
      <Row className="gx-3">
        <Col xxl={9}>
          <Row className="gx-3 h-100">
            <Col lg={2} className="mb-3 mb-xxl-0">
              <EmployeeSelect
                options={options.employees}
                value={selectedEmployee}
                onChange={(employeeObj) => {
                  if (selfPermission) return;
                  setSelectedEmployee(employeeObj);
                }}
                isLoading={loadingOptions}
                disabled={selfPermission}
              />
            </Col>
            <Col lg={3} className="mb-3 mb-xxl-0">
              <div className="position-relative rounder-4px mb-3 mb-xxl-0 h-100">
                <input
                  type="text"
                  id="product-category-field"
                  className="text-input-mepos mepos-input form-control text-dark fs-7 rounder-4px h-100 cursor-pointer pe-5"
                  placeholder={t('salesFilters.Category')}
                  value={currentCatNameFilter}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsPickerOpen((prev) => !prev);
                  }}
                  readOnly
                />
                <i
                  className={`bi bi-chevron-down position-absolute top-50 end-0 text-dark me-3 pointer-event chevron-icon ${isPickerOpen ? 'rotated' : ''}`}></i>
                {options.categories && (
                  <div
                    ref={dropdownRef}
                    className={`dropdown me-pos-primary position-absolute opacity-1 top-10 z-5 start-0 end-0 mt-1 bg-white shadow ${isPickerOpen ? 'p-2' : ''}`}
                  >
                    <CategoryPickerComponent
                      isOpen={isPickerOpen}
                      locale={i18n.language}
                      onSearchChange={(searchValue) => {}}
                      onChangeCategory={(currentCategory) => {
                        if (currentCategory.isPicked) {
                          setCurrentCatNameFilter(currentCategory?.name);
                          handleCategoryChange(currentCategory?.id, currentCategory?.name);
                          setIsPickerOpen(false);
                        } else {
                          setCurrentCatNameFilter('');
                        }
                      }}
                      onChangeSubcategory={(currentSubcategory, currentCategory) => {
                        if (currentSubcategory?.isPicked) {
                          setCurrentCatNameFilter(currentSubcategory?.name);
                          handleSubCategoryChange(currentCategory?.id, currentCategory?.name, currentSubcategory?.id, currentSubcategory?.name);
                          setIsPickerOpen(false);
                        } else {
                          setCurrentCatNameFilter('');
                        }
                      }}
                      serviceCategories={options.categories}
                    />
                  </div>)}
              </div>
            </Col>
            <Col lg={2} className="mb-3 mb-xxl-0">
              <BrandSelect
                options={options.brands}
                value={selectedBrand}
                onChange={(key) => setSelectedBrand(key)}
                isLoading={loadingOptions}
              />
            </Col>
            <Col lg={3} className="mb-3 mb-xxl-0">
              <TextInputWithAutoComplete
                placeholder={t('salesFilters.Product / SKU / Barcode')}
                value={selectedProductSkuBarcode}
                onChange={setSelectedProductSkuBarcode}
                suggestions={productSkuBarcodeSuggestions}
              />
            </Col>
            <Col lg={2} className="mb-3 mb-xxl-0">
              <WarehouseSelect
                options={options.warehouses}
                value={selectedWarehouse}
                onChange={key => setSelectedWarehouse(key)}
                isLoading={loadingOptions}
              />
            </Col>
          </Row>
        </Col>
        <Col lg={6} xxl={3} className="ms-lg-auto">
          <Row className="gx-3 h-100">
            <Col
              xs={6}
              className="mb-3 mb-lg-0">
              <Button
                type="button"
                variant="outline-dark"
                className="clear-btn w-100 h-100 px-1 fw-normal border-info rounded-1"
                onClick={handleResetFilter}
              >
                {t('salesFilters.Clear')}
              </Button>
            </Col>
            <Col
              xs={6}
              className="mb-3 mb-lg-0">
              <Button
                variant="pos-main"
                className="w-100 h-100 px-1 fw-normal rounded-1"
                onClick={handleApplyFilter}
              >
                {t('salesFilters.Generate')}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default MePosSaleFilters;